import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Send, Trash2, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { MigrationComment } from '../types';

interface ProjectCommentsProps {
  projectId: number;
  className?: string;
}

function initials(firstname?: string, lastname?: string): string {
  const f = firstname?.[0] ?? '';
  const l = lastname?.[0] ?? '';
  return `${f}${l}`.toUpperCase() || '?';
}

function authorName(author: MigrationComment['author']): string {
  if (!author) return 'Utilisateur supprimé';
  return `${author.firstname} ${author.lastname}`.trim() || `#${author.id}`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH} h`;
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Fil de discussion d'un projet de migration : échange entre le propriétaire
 * assigné et les administrateurs (ex. documenter un blocage, une décision…).
 * L'accès est contrôlé côté API (admin ou propriétaire) ; le composant n'est
 * donc affiché que lorsque l'utilisateur peut y accéder.
 */
export function ProjectComments({ projectId, className }: ProjectCommentsProps) {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('action.migration_v1_v2.manage');

  const [comments, setComments] = useState<MigrationComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await migrationV1V2Api.getComments(projectId);
      setComments(data);
    } catch {
      setError('Erreur lors du chargement des commentaires');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const canDelete = (comment: MigrationComment) =>
    canManage || (!!comment.author && comment.author.id === user?.id);

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed || posting) return;
    try {
      setPosting(true);
      setError('');
      const created = await migrationV1V2Api.createComment(projectId, trimmed);
      setComments((prev) => [...prev, created]);
      setBody('');
    } catch {
      setError("Échec de l'envoi du message");
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      await migrationV1V2Api.deleteComment(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setError('Échec de la suppression du commentaire');
    } finally {
      setDeletingId(null);
    }
  };

  // Cmd/Ctrl + Entrée pour envoyer.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm',
        className
      )}
    >
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-slate-700">
        <MessageSquare className="h-4.5 w-4.5 text-violet-600" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Commentaires</h2>
        {!loading && comments.length > 0 && (
          <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-slate-700 rounded-full px-2 py-0.5">
            {comments.length}
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-14 rounded-lg" />
            <Skeleton className="h-14 rounded-lg" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-2">
            Aucun message pour l'instant. Laissez un mot — par exemple la raison d'un blocage.
          </p>
        ) : (
          <ul className="space-y-4">
            {comments.map((comment) => (
              <li key={comment.id} className="flex gap-3 group">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300">
                    {initials(comment.author?.firstname, comment.author?.lastname)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {authorName(comment.author)}
                    </span>
                    <span className="text-xs text-gray-400">{formatWhen(comment.createdAt)}</span>
                    {canDelete(comment) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(comment.id)}
                        disabled={deletingId === comment.id}
                        className="ml-auto text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        aria-label="Supprimer le commentaire"
                      >
                        {deletingId === comment.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {comment.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Composer */}
        <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Écrire un message… (Cmd/Ctrl + Entrée pour envoyer)"
            rows={3}
            className="resize-none"
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={handleSubmit} disabled={!body.trim() || posting}>
              {posting ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Envoyer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
